import { ArticleItem } from '../../types';

export const threeMedicalDeviceReviewGuidelinesEn: ArticleItem = {
  slug: "three-medical-device-review-guidelines",
  title: "Three Medical Device Registration Review Guidelines Released",
  summary: "Reposted article: CMDE released draft review guidelines for three medical device categories, further clarifying scope and review focus areas.",
  seo: {
    title: "Three Medical Device Registration Review Guidelines Released | HL Medical",
    description: "Read about CMDE draft review guidelines for meniscus suture systems, orthodontic anchorage screws, and artificial cervical intervertebral disc prostheses.",
    keywords: "medical device registration China, CMDE guidelines, NMPA registration, medical device review, HL Medical",
    canonicalUrl: "https://hlmedicalconsult.cn/en/articles/three-medical-device-review-guidelines",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/three-medical-device-review-guidelines",
      cn: "https://hlmedicalconsult.cn/cn/articles/three-medical-device-review-guidelines",
      xDefault: "https://hlmedicalconsult.cn/en/articles/three-medical-device-review-guidelines"
    },
    ogTitle: "Three Medical Device Registration Review Guidelines Released",
    ogDescription: "CMDE draft review guidelines clarify registration review focus areas for three medical device categories.",
    ogType: "article"
  },
  date: "2026-05-06",
  category: "Regulatory Update",
  readTime: "5 min read",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: <a href='https://www.highusability.com' target='_blank' rel='noopener noreferrer'>www.high-usability.com</a>",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "Registration Review Focus Areas" },
    { type: "paragraph", text: "The review framework for these three device categories is essentially the same, covering six major parts: regulatory information, overview materials, non-clinical materials, clinical evaluation materials, instructions for use and label samples, and quality management system documents." },
    { type: "heading", text: "Regulatory Information" },
    { type: "paragraph", text: "For meniscus suture systems and orthodontic anchorage screws, applicants need to clearly explain:" },
    { type: "bullet", text: "Product name" },
    { type: "bullet", text: "Classification code" },
    { type: "bullet", text: "Registration unit division" },
    { type: "paragraph", text: "Artificial cervical intervertebral disc prostheses require one additional layer of detail: model specifications, structure, and composition must also be described." },
    { type: "heading", text: "Overview Materials" },
    { type: "paragraph", text: "For all three device categories, applicants should clearly explain:" },
    { type: "bullet", text: "Product description" },
    { type: "bullet", text: "Model specifications" },
    { type: "bullet", text: "Packaging information" },
    { type: "bullet", text: "Reference and comparison with similar and/or previous-generation products" },
    { type: "bullet", text: "Scope of application and contraindications" },
    { type: "bullet", text: "Marketing history of the submitted product" },
    { type: "paragraph", text: "Meniscus suture systems and orthodontic anchorage screws also specifically emphasize operating principles and development history. Meniscus suture systems additionally include instruments used together with the product." },
    { type: "paragraph", text: "The logic is straightforward: it is not enough to describe the product itself. Applicants also need to explain how the product is used." },
    { type: "heading", text: "Non-clinical Materials" },
    { type: "paragraph", text: "This section becomes more technical. All three device categories require:" },
    { type: "bullet", text: "Product risk management materials" },
    { type: "bullet", text: "Essential principles checklist for medical device safety and performance" },
    { type: "bullet", text: "Product technical requirements" },
    { type: "bullet", text: "Product testing" },
    { type: "bullet", text: "Research materials" },
    { type: "paragraph", text: "In general, this part is used to demonstrate device safety." },
    { type: "heading", text: "Clinical Evaluation Materials" },
    { type: "paragraph", text: "This is where the three device categories differ the most. Meniscus suture systems and orthodontic anchorage screws are relatively conventional: substantial equivalence comparison and clinical trials." },
    { type: "paragraph", text: "Artificial cervical intervertebral disc prostheses are different. The requirements are more detailed, including:" },
    { type: "bullet", text: "Clinical trial design type" },
    { type: "bullet", text: "Inclusion and exclusion criteria" },
    { type: "bullet", text: "Subject withdrawal criteria and handling of withdrawn subjects" },
    { type: "bullet", text: "Clinical trial duration and visit windows" },
    { type: "bullet", text: "Clinical trial endpoints and evaluation criteria" },
    { type: "bullet", text: "Selection of control products" },
    { type: "bullet", text: "Sample size estimation" },
    { type: "bullet", text: "Demographic and baseline characteristics" },
    { type: "bullet", text: "Statistical analysis methods" },
    { type: "paragraph", text: "In other words, the question is no longer simply whether a clinical trial is needed. The framework for how to conduct the clinical trial has already been laid out." },
    { type: "heading", text: "The Other Two Major Sections" },
    { type: "paragraph", text: "Instructions for use and label samples: all three guidelines clarify the role of the Provisions on the Administration of Medical Device Instructions and Labels and standards such as YY/T 0466.1. Quality management system documents should be prepared with reference to the announcement on medical device registration submission requirements and approval document formats." },
    { type: "section", text: "02" },
    { type: "heading", text: "Conclusion" },
    { type: "paragraph", text: "The three newly released medical device guidelines share the same broad framework, while the artificial cervical intervertebral disc prosthesis has more specific requirements in the clinical evaluation section. The consultation notice also includes a feedback form, giving relevant companies and professionals a channel to participate in improving the guidelines and supporting smoother review work for these devices." }
  ]
};

export const threeMedicalDeviceReviewGuidelinesCn: ArticleItem = {
  slug: "three-medical-device-review-guidelines",
  title: "三大医疗器械注册审评指导原则出炉",
  summary: "转载文章：器审中心就三类医疗器械注册审评指导原则征求意见，适用范围和审查要点进一步明确。",
  seo: {
    title: "三大医疗器械注册审评指导原则出炉 | 赫莱医药",
    description: "器审中心就半月板缝合系统、正畸支抗钉、人工颈椎间盘假体三类器械注册审评指导原则征求意见。",
    keywords: "医疗器械注册, 器审中心, 注册审评指导原则, NMPA注册, 赫莱医药",
    canonicalUrl: "https://hlmedicalconsult.cn/cn/articles/three-medical-device-review-guidelines",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/three-medical-device-review-guidelines",
      cn: "https://hlmedicalconsult.cn/cn/articles/three-medical-device-review-guidelines",
      xDefault: "https://hlmedicalconsult.cn/en/articles/three-medical-device-review-guidelines"
    },
    ogTitle: "三大医疗器械注册审评指导原则出炉",
    ogDescription: "三类医疗器械注册审评指导原则征求意见，适用范围和审查要点进一步明确。",
    ogType: "article"
  },
  date: "2026-05-06",
  category: "法规动态",
  readTime: "约5分钟",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: <a href='https://www.highusability.com' target='_blank' rel='noopener noreferrer'>www.high-usability.com</a>",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "注册审查要点" },
    { type: "paragraph", text: "这三类器械的审查框架其实是一样的，都为六大块：监管信息、综述资料、非临床资料、临床评价资料、产品说明书和标签样稿与质量管理体系文件。" },
    { type: "heading", text: "监管信息" },
    { type: "paragraph", text: "半月板缝合系统、正畸支抗钉都要说清楚：" },
    { type: "bullet", text: "产品名称" },
    { type: "bullet", text: "分类编码" },
    { type: "bullet", text: "注册单元划分" },
    { type: "paragraph", text: "而人工颈椎间盘假体多了一层，还要求详述型号规格与结构组成。" },
    { type: "heading", text: "综述资料" },
    { type: "paragraph", text: "三款器械都要求你把这些讲清楚：" },
    { type: "bullet", text: "产品描述" },
    { type: "bullet", text: "型号规格" },
    { type: "bullet", text: "包装说明" },
    { type: "bullet", text: "与同类和/或前代产品的参考和比较" },
    { type: "bullet", text: "适用范围和禁忌证" },
    { type: "bullet", text: "申报产品上市历史" },
    { type: "paragraph", text: "半月板缝合系统和正畸支抗钉还专门强调了“操作原理”与\"研发历程\"，其中半月板缝合系统还多了配合使用器械这一项。" },
    { type: "paragraph", text: "其实道理很直白：不仅要说产品本身，还要说它是怎么被用起来的。" },
    { type: "heading", text: "非临床资料" },
    { type: "paragraph", text: "到了这部分，就开始有点“硬核”了。三款器械都要求提供：" },
    { type: "bullet", text: "产品风险管理资料" },
    { type: "bullet", text: "医疗器械安全和性能基本原则清单" },
    { type: "bullet", text: "产品技术要求" },
    { type: "bullet", text: "产品检验" },
    { type: "bullet", text: "研究资料" },
    { type: "paragraph", text: "总得来说，这部分资料就是让我们证明器械安全。" },
    { type: "heading", text: "临床评价资料" },
    { type: "paragraph", text: "这三类器械，在这部分上差距最大。半月板缝合系统和正畸支抗钉相对“常规”：走同品种对比，做临床试验。" },
    { type: "paragraph", text: "而人工颈椎间盘假体则完全不同，要求更细，包括：" },
    { type: "bullet", text: "临床试验设计类型" },
    { type: "bullet", text: "入选、排除标准" },
    { type: "bullet", text: "受试者退出标准及退出受试者的处理" },
    { type: "bullet", text: "临床试验持续时间与窗口期" },
    { type: "bullet", text: "临床试验评价指标及判定标准" },
    { type: "bullet", text: "对照产品的选择" },
    { type: "bullet", text: "样本量的估算" },
    { type: "bullet", text: "人口统计学和基线特征" },
    { type: "bullet", text: "统计分析方法" },
    { type: "paragraph", text: "可以说，已经不是“做不做临床”的问题，而是临床怎么做，给你写好了框架。" },
    { type: "heading", text: "另两大板块" },
    { type: "paragraph", text: "产品说明书和标签样稿：三款器械指导原则均明确了《医疗器械说明书和标签管理规定》和YY/T 0466.1等标准的指导作用。质量管理体系文件撰写均需要参考《关于公布医疗器械注册申报资料要求和批准证明文件格式的公告》。" },
    { type: "section", text: "02" },
    { type: "heading", text: "结论" },
    { type: "paragraph", text: "本次发布的三项医疗器械指导原则大框架一致，其中人工颈椎间盘假体在临床评价部分要求更为具体。本次征求意见通知同时附带意见反馈表，为相关企业和从业者提供了参与完善指导原则的渠道，也推动相关器械审查工作的顺利进行。" }
  ]
};
